import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatETH, formatAddress } from '../../context/WalletContext';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import TelegramShareButton from '../TelegramShareButton';
import { Clock, TrendingUp, User, Trophy, XCircle, Timer, Coins, Send } from 'lucide-react';
import { formatDistanceToNow, isPast } from 'date-fns';
import { fr, es, de, it, pt, ja, ko, zhCN, ar, hi, ru, tr, pl, nl, uk, vi, th, sv, ro, cs, sk, hu, nb, da, fi, id, he, enUS } from 'date-fns/locale';

const DATE_LOCALES = {
    en: enUS, fr, es, de, it, pt, ja, ko, zh: zhCN, ar, hi, ru, tr, pl, nl, uk, vi, th, sv, ro, cs, sk, hu, no: nb, da, fi, id, he,
};

const statusConfig = {
    open: { labelKey: 'betCard.statusOpen', color: 'bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/20', icon: Timer },
    active: { labelKey: 'betCard.statusActive', color: 'bg-[#6F3FCA]/20 text-[#9B6FE8] border-[#6F3FCA]/30', icon: Timer },
    in_assertion: { labelKey: 'betCard.statusInAssertion', color: 'bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/30', icon: Clock },
    resolved: { labelKey: 'betCard.statusResolved', color: 'bg-[#6F3FCA]/20 text-[#9B6FE8] border-[#6F3FCA]/30', icon: Trophy },
    cancelled: { labelKey: 'betCard.statusCancelled', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
};

const categoryConfig = {
    sports: { labelKey: 'bets.catSports' },
    crypto: { labelKey: 'bets.catCrypto' },
    politics: { labelKey: 'bets.catPolitics' },
    entertainment: { labelKey: 'bets.catEntertainment' },
    custom: { labelKey: 'bets.catCustom' },
};

export function BetCard({ bet, showActions = true, userAddress, shareCount = 0 }) {
    const { t, i18n } = useTranslation();
    const dateLocale = DATE_LOCALES[i18n.language] || DATE_LOCALES[i18n.language?.split('-')[0]] || enUS;
    const status = statusConfig[bet.status] || statusConfig.open;
    const category = categoryConfig[bet.category] || categoryConfig.custom;
    const StatusIcon = status.icon;
    
    const isCreator = userAddress?.toLowerCase() === bet.creator_address?.toLowerCase();
    const totalPool = (bet.creator_stake || 0) + (bet.taker_stake || 0);
    const currency = (bet.currency || 'ETH').toUpperCase();
    const currencyColor = currency === 'B4P' ? '#FFD800' : currency === 'USDC' ? '#2775CA' : '#627EEA';
    const formatAmount = (n) => {
        if (n == null) return '0';
        const v = parseFloat(n) || 0;
        if (currency === 'ETH') return v.toFixed(4);
        if (currency === 'USDC') return v.toFixed(2);
        return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    };

    return (
        <div 
            className="rounded-2xl bg-white/[0.02] border border-white/5 hover:border-[#00FF66]/20 transition-all duration-300 overflow-hidden group card-hover"
            data-testid={`bet-card-${bet.id}`}
        >
            {/* Top accent line */}
            <div className="h-1 bg-gradient-to-r from-[#6F3FCA] via-[#00FF66] to-[#FFD700]" />
            
            <div className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className={`text-xs ${status.color}`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {t(status.labelKey)}
                            </Badge>
                            <Badge variant="outline" className="text-xs bg-white/5 text-white/50 border-white/10">
                                {t(category.labelKey)}
                            </Badge>
                            <Badge
                                variant="outline"
                                className="text-xs font-bold border-0"
                                style={{ background: `${currencyColor}20`, color: currencyColor, borderColor: `${currencyColor}40` }}
                                data-testid={`bet-currency-${bet.id}`}
                            >
                                {currency}
                                {currency === 'B4P' && <span className="ml-1 text-[9px] opacity-80">×2</span>}
                            </Badge>
                            {shareCount > 0 && (
                                <Badge variant="outline" className="text-xs bg-[#00FFFF]/10 text-[#00FFFF] border-[#00FFFF]/30 flex items-center gap-1" data-testid={`share-count-${bet.id}`}>
                                    <Send className="w-3 h-3" /> {shareCount}
                                </Badge>
                            )}
                        </div>
                        <h3 className="font-medium text-white line-clamp-2 group-hover:text-[#00FF66] transition-colors">
                            {bet.description}
                        </h3>
                    </div>
                    
                    {/* Odds Badge */}
                    <div className="flex-shrink-0 text-center px-3 py-2 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                        <div className="font-mono text-xl font-bold text-[#00FF66]">
                            {bet.odds_display?.toFixed(2)}x
                        </div>
                        <div className="text-[10px] text-white/40 uppercase">{t('betCard.odds')}</div>
                    </div>
                </div>

                {/* Stakes Info */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                        <div className="flex items-center gap-1 text-white/40 text-[10px] mb-1">
                            <Coins className="w-3 h-3" />
                            {t('betCard.stake')}
                        </div>
                        <div className="font-mono text-white text-sm font-medium">
                            {formatAmount(bet.creator_stake)} {currency}
                        </div>
                    </div>
                    <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                        <div className="flex items-center gap-1 text-white/40 text-[10px] mb-1">
                            <TrendingUp className="w-3 h-3" />
                            {t('betCard.toWin')}
                        </div>
                        <div className="font-mono text-[#00FF66] text-sm font-medium">
                            {formatAmount(bet.taker_stake)} {currency}
                        </div>
                    </div>
                    <div className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                        <div className="flex items-center gap-1 text-white/40 text-[10px] mb-1">
                            <Coins className="w-3 h-3" />
                            {t('betCard.pool')}
                        </div>
                        <div className="font-mono text-[#FFD700] text-sm font-medium">
                            {formatAmount(totalPool)} {currency}
                        </div>
                    </div>
                </div>

                {/* Creator & Time */}
                <div className="flex items-center justify-between text-xs text-white/40 mb-4">
                    <div className="flex items-center gap-1.5">
                        <User className="w-3 h-3" />
                        <span className="font-mono">{formatAddress(bet.creator_address)}</span>
                        {isCreator && (
                            <Badge className="text-[10px] bg-[#00FF66]/10 text-[#00FF66] border-0 px-1.5 py-0">
                                {t('betCard.you')}
                            </Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        <span>
                            {isPast(new Date(bet.resolution_time)) ? `${t('betCard.ended')} ` : `${t('betCard.ends')} `}
                            {formatDistanceToNow(new Date(bet.resolution_time), { addSuffix: true, locale: dateLocale })}
                        </span>
                    </div>
                </div>

                {/* Winner info for resolved bets */}
                {bet.status === 'resolved' && bet.winner_address && (
                    <div className="mb-4 p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Trophy className="w-4 h-4 text-[#00FF66]" />
                                <span className="text-[#00FF66] text-sm font-medium">{t('betCard.winner')}</span>
                                <span className="font-mono text-white text-sm">{formatAddress(bet.winner_address)}</span>
                            </div>
                            <div className="font-mono text-[#00FF66] font-bold">
                                +{formatAmount(bet.payout_amount)} {currency}
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions */}
                {showActions && (
                    <div className="flex gap-2">
                        <Link to={`/bet/${bet.id}`} className="flex-1">
                            <Button 
                                variant="outline" 
                                className="w-full border-white/10 text-white hover:bg-white/5 hover:border-white/20"
                                data-testid={`view-bet-${bet.id}`}
                            >
                                {t('betCard.viewDetails')}
                            </Button>
                        </Link>
                        {bet.status === 'open' && !isCreator && (
                            <Link to={`/bet/${bet.id}`} className="flex-1">
                                <Button 
                                    className="w-full bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] hover:shadow-[0_0_15px_rgba(130,255,122,0.3)]"
                                    data-testid={`take-bet-${bet.id}`}
                                >
                                    {t('betCard.takeBet')}
                                </Button>
                            </Link>
                        )}
                        <TelegramShareButton
                            title={bet.description || `Bet ${bet.id?.slice(0, 8)}`}
                            subtitle={t('betCard.shareSubtitle', { stake: formatAmount(bet.creator_stake), currency, odds: (bet.odds_bps / 10000).toFixed(2) })}
                            path={`/bet/${bet.id}`}
                            tone="cyan"
                            size="icon"
                            testId={`share-bet-${bet.id}`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
